import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiGet } from '../utils/api';
import cache from '../utils/cache';
import { 
  registerLogoutCallback, 
  registerGetTokenCallback, 
  registerGetCurrentUserCallback 
} from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Auth0 hooks
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout
  } = useAuth0();

  const [userInfo, setUserInfo] = useState(null); // Our user info with userId
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState(null);
  const isLoggingOutRef = useRef(false);
  
  // Use refs to always have latest values in callbacks (avoids stale closures)
  const auth0UserRef = useRef(auth0User);
  const userInfoRef = useRef(userInfo);
  
  // Keep refs in sync with state
  useEffect(() => {
    auth0UserRef.current = auth0User;
    
    // Log Auth0 user object from SDK (especially for Google OAuth debugging)
    if (auth0User) {
      console.log('[Auth0 Frontend] Auth0 user object from SDK:', {
        sub: auth0User.sub,
        email: auth0User.email,
        email_verified: auth0User.email_verified,
        name: auth0User.name,
        nickname: auth0User.nickname,
        given_name: auth0User.given_name,
        family_name: auth0User.family_name,
        picture: auth0User.picture,
        hasEmail: !!auth0User.email,
        allKeys: Object.keys(auth0User),
        fullObject: JSON.stringify(auth0User, null, 2)
      });
    }
  }, [auth0User]);
  
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  // Demo mode state - persists per tab using sessionStorage
  const [isDemoMode, setIsDemoMode] = useState(() => {
    return sessionStorage.getItem('theraengage_demo_mode') === 'true';
  });

  // Check if user has a valid token
  const isUserAuthenticated = () => {
    return isAuthenticated && !!auth0User && !!userInfo;
  };

  // Get user info from our system (with secure caching)
  const getUserInfo = async (authUser) => {
    const startTime = Date.now();
    const authId = authUser.sub;
    
    // Helper to track events to PostHog
    const trackAuthEvent = async (eventName, properties = {}) => {
      try {
        const analyticsService = (await import('../services/analyticsService')).default;
        if (analyticsService.isInitialized) {
          analyticsService.track(eventName, {
            authId: authId.substring(0, 20) + '...', // Partial ID for privacy
            ...properties,
            timestamp: new Date().toISOString(),
            isPWA: window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true,
            userAgent: navigator.userAgent,
            online: navigator.onLine,
            connectionType: navigator.connection?.effectiveType || 'unknown'
          });
        }
      } catch (error) {
        // Don't break auth flow if analytics fails
        console.error('Failed to track auth event:', error);
      }
    };
    
    try {
      console.log('🔔 getUserInfo called for:', authId);
      await trackAuthEvent('auth_get_user_info_started', {
        hasAuthId: !!authId
      });
      
      // Check cache first (1 hour TTL)
      const cached = cache.get('user', authId);
      if (cached && cached.user) {
        console.log('✅ Using cached user info');
        await trackAuthEvent('auth_user_info_cache_hit', {
          cacheAge: cached.timestamp ? Date.now() - cached.timestamp : 0
        });
        return cached.user;
      }
      
      await trackAuthEvent('auth_user_info_cache_miss');
      
      // Get token for API call with timeout to prevent infinite hanging on mobile PWA
      let token = null;
      const tokenStartTime = Date.now();
      try {
        console.log('🔑 Attempting silent token refresh...');
        await trackAuthEvent('auth_token_refresh_started');
        
        // Wrap getAccessTokenSilently with timeout (10 seconds)
        // This prevents infinite hanging when iframe-based silent refresh fails on mobile PWA
        const TOKEN_REFRESH_TIMEOUT = 10000; // 10 seconds
        const tokenPromise = getAccessTokenSilently();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token refresh timeout - silent refresh took too long')), TOKEN_REFRESH_TIMEOUT)
        );
        
        token = await Promise.race([tokenPromise, timeoutPromise]);
        const tokenDuration = Date.now() - tokenStartTime;
        console.log('✅ Token received successfully');
        await trackAuthEvent('auth_token_refresh_success', {
          duration: tokenDuration,
          hasToken: !!token
        });
      } catch (tokenError) {
        const tokenDuration = Date.now() - tokenStartTime;
        console.error('❌ Error getting access token:', tokenError);
        
        // Check for specific Auth0 errors
        const errorMessage = tokenError.message || tokenError.error || '';
        const errorType = errorMessage.includes('timeout') ? 'timeout' :
                         errorMessage.includes('login_required') ? 'login_required' :
                         errorMessage.includes('consent_required') ? 'consent_required' :
                         errorMessage.includes('Service not found') ? 'config_error' :
                         'unknown';
        
        await trackAuthEvent('auth_token_refresh_failed', {
          duration: tokenDuration,
          errorType: errorType,
          errorMessage: errorMessage.substring(0, 200), // Limit length
          hasError: !!tokenError.error
        });
        
        // Check for timeout - this is the main issue on mobile PWA
        if (errorMessage.includes('timeout') || errorMessage.includes('Token refresh timeout')) {
          console.warn('⚠️ Silent token refresh timed out - falling back to redirect login');
          await trackAuthEvent('auth_token_refresh_timeout', {
            duration: tokenDuration,
            willRedirect: true
          });
        }
        
        // Check for configuration errors - don't redirect for these
        if (errorMessage.includes('Service not found') || errorMessage.includes('access_denied')) {
          throw new Error('Authentication configuration error: API not configured. Please contact support.');
        }
        
        // For ALL other token refresh failures (timeout, login_required, unknown errors),
        // fall back to redirect login - this is more reliable on mobile PWA
        // where iframe-based refresh often fails for various reasons
        console.warn('⚠️ Token refresh failed - falling back to redirect login', {
          errorType: errorType,
          errorMessage: errorMessage.substring(0, 100)
        });
        
        try {
          await trackAuthEvent('auth_redirect_login_attempted', {
            reason: errorType === 'timeout' ? 'timeout' : 
                   errorType === 'login_required' ? 'login_required' : 
                   'token_refresh_failed',
            returnTo: window.location.pathname,
            originalErrorType: errorType
          });
          
          await loginWithRedirect({
            appState: {
              returnTo: window.location.pathname + window.location.search
            }
          });
          // Will redirect, so this won't execute, but return to satisfy linter
          return null;
        } catch (redirectError) {
          console.error('Error during redirect login:', redirectError);
          await trackAuthEvent('auth_redirect_login_failed', {
            error: redirectError.message?.substring(0, 200),
            originalErrorType: errorType
          });
          throw new Error('Failed to refresh authentication. Please try logging in again.');
        }
      }
      
      // Fetch fresh data from server
      console.log('🌐 Fetching fresh user data from server...');
      const apiStartTime = Date.now();
      await trackAuthEvent('auth_api_call_started', {
        endpoint: 'get-current-user'
      });
      
      const response = await apiGet('/.netlify/functions/get-current-user', null, token);
      const apiDuration = Date.now() - apiStartTime;
      
      await trackAuthEvent('auth_api_call_success', {
        duration: apiDuration,
        needsRoleSelection: response.needsRoleSelection || false,
        hasUser: !!response.user,
        hasUserId: !!response.user?.userId
      });
      
      // Check if user needs to select a role
      if (response.needsRoleSelection) {
        const userData = {
          ...response.user,
          needsRoleSelection: true
        };
        
        // Don't cache if role selection is needed
        return userData;
      }
      
      // Cache the result (1 hour TTL - role could change but rarely)
      cache.set('user', { user: response.user }, 60, authId);
      console.log('💾 Cached user info');
      
      const totalDuration = Date.now() - startTime;
      await trackAuthEvent('auth_get_user_info_success', {
        totalDuration: totalDuration,
        usedCache: false,
        hasRole: !!response.user?.role
      });
      
      return response.user;
    } catch (error) {
      const totalDuration = Date.now() - startTime;
      console.error('Error getting user info:', error);
      
      await trackAuthEvent('auth_get_user_info_failed', {
        totalDuration: totalDuration,
        errorMessage: error.message?.substring(0, 200),
        errorType: error.name || 'Error'
      });
      
      throw error;
    }
  };

  // Refresh user info (useful after role selection or profile updates)
  const refreshUserInfo = async () => {
    if (!auth0User) {
      console.warn('Cannot refresh user info: no user logged in');
      return;
    }
    try {
      const authId = auth0User.sub;
      
      // Clear cache before refreshing
      cache.delete('user', authId);
      
      const updatedUserInfo = await getUserInfo(auth0User);
      setUserInfo(updatedUserInfo);
      return updatedUserInfo;
    } catch (error) {
      console.error('Error refreshing user info:', error);
      throw error;
    }
  };

  // Get therapist info (NO CACHING - always fresh data)
  const getTherapistInfo = async (therapistId) => {
    try {
      console.log('🔔 getTherapistInfo called for:', therapistId);
      
      // Always fetch fresh data from server
      console.log('🌐 Fetching fresh therapist data from server...');
      const response = await apiGet(`/.netlify/functions/get-therapist-info?therapistId=${therapistId}`);
      
      const therapistData = {
        userId: response.therapist.id,
        name: response.therapist.name,
        email: response.therapist.email,
        role: response.therapist.role
      };
      
      return therapistData;
    } catch (error) {
      console.error('Error getting therapist info:', error);
      throw error;
    }
  };

  // Toggle demo mode (for therapist viewing patient portal)
  const toggleDemoMode = (value) => {
    setIsDemoMode(value);
    if (value) {
      sessionStorage.setItem('theraengage_demo_mode', 'true');
    } else {
      sessionStorage.removeItem('theraengage_demo_mode');
    }
  };

  // Internal logout cleanup function
  const performLogoutCleanup = () => {
    // Track logout event before clearing user data
    try {
      import('../services/analyticsService').then(({ default: analyticsService }) => {
        analyticsService.track('user_logout', {
          userId: userInfo?.userId,
          role: userInfo?.role
        });
        analyticsService.reset();
      }).catch(() => {
        // Don't break logout if analytics fails
      });
    } catch (analyticsError) {
      // Don't break logout if analytics fails
    }
    
    // Clear all cache on logout for security
    const authId = auth0User?.sub;
    if (authId) {
      cache.clearUserCache(authId);
    }
    cache.clearAll();
    
    setUserInfo(null);
    setAuthReady(false);
    setError(null);
    
    // Clear demo mode on logout
    sessionStorage.removeItem('theraengage_demo_mode');
    setIsDemoMode(false);
  };

  // Auth methods
  const login = async () => {
    try {
      setError(null);
      await loginWithRedirect({
        screen_hint: 'login'
      });
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    }
  };

  const signup = async () => {
    try {
      setError(null);
      await loginWithRedirect({
        screen_hint: 'signup'
      });
    } catch (error) {
      console.error('Signup error:', error);
      setError('Signup failed. Please try again.');
    }
  };

  const logout = () => {
    // Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    
    // Perform cleanup
    performLogoutCleanup();
    
    // Call Auth0 logout
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
    
    // Reset logout guard after a delay
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  };

  // Register callbacks for authService
  useEffect(() => {
    registerLogoutCallback(() => {
      logout();
    });
  }, []);

  // Register token callbacks - use refs to avoid stale closures
  useEffect(() => {
    registerGetTokenCallback(async () => {
      // Use ref to get latest value
      const currentUser = auth0UserRef.current;
      if (!currentUser) {
        return null;
      }
      
      try {
        const token = await getAccessTokenSilently();
        return token;
      } catch (error) {
        console.error('Error getting token in callback:', error);
        return null;
      }
    });
    
    registerGetCurrentUserCallback(() => {
      // Return Auth0 user object in format compatible with existing code
      const currentUser = auth0UserRef.current;
      if (!currentUser) {
        return null;
      }
      
      // Return Auth0 user object in standardized format
      return {
        sub: currentUser.sub,
        id: currentUser.sub, // Alias for compatibility
        email: currentUser.email,
        name: currentUser.name,
        ...currentUser
      };
    });
  }, []);

  // Handle Auth0 authentication state changes
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      setError(null);

      // Check for Auth0 errors in URL (from redirect callback)
      const urlParams = new URLSearchParams(window.location.search);
      const auth0Error = urlParams.get('error');
      const auth0ErrorDescription = urlParams.get('error_description');
      
      if (auth0Error) {
        console.error('Auth0 authentication error:', auth0Error, auth0ErrorDescription);
        
        // Check for "Service not found" error
        if (auth0ErrorDescription?.includes('Service not found') || auth0ErrorDescription?.includes('access_denied')) {
          setError('Authentication configuration error: API not found. Please contact support.');
          setLoading(false);
          setAuthReady(false);
          // Clear error from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        
        setError(`Authentication failed: ${auth0ErrorDescription || auth0Error}`);
        setLoading(false);
        setAuthReady(false);
        // Clear error from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      if (auth0Loading) {
        // Still loading Auth0 state
        return;
      }

      if (!isAuthenticated || !auth0User) {
        // Not authenticated
        setLoading(false);
        setAuthReady(true);
        return;
      }

      // User is authenticated - get user info
      try {
        const info = await getUserInfo(auth0User);
        
        // If getUserInfo returns null, it means we're redirecting to login
        // Don't set state in this case as the redirect will happen
        if (info === null) {
          return; // Redirect is happening, don't update state
        }
        
        setUserInfo(info);
        setAuthReady(true);
        
        // Track analytics events
        try {
          const analyticsService = (await import('../services/analyticsService')).default;
          
          const isNewSignup = !info?.role;
          
          if (info?.userId) {
            // Get cache version from sessionStorage (set by service worker)
            const cacheVersion = sessionStorage.getItem('sw_cache_version') || 'unknown';
            analyticsService.identify(info.userId, {
              role: info.role,
              cacheVersion: cacheVersion
            });
            
            queueMicrotask(() => {
              if (isNewSignup) {
                analyticsService.track('signup_completed', {
                  source: 'landing_page',
                  signupMethod: 'auth0'
                });
              }
              
              analyticsService.track('user_login', {
                userId: info?.userId,
                role: info?.role,
                isNewSignup: isNewSignup
              });
            });
          } else {
            if (isNewSignup) {
              analyticsService.track('signup_completed', {
                source: 'landing_page',
                signupMethod: 'auth0'
              });
            }
            analyticsService.track('user_login', {
              userId: info?.userId,
              role: info?.role,
              isNewSignup: isNewSignup
            });
          }
        } catch (analyticsError) {
          console.error('Analytics tracking error:', analyticsError);
        }
      } catch (error) {
        console.error('Error getting user info on init:', error);
        
        const errorMessage = error.message || '';
        
        // If error message indicates redirect is happening, don't set error state
        // The redirect will handle the flow
        if (errorMessage.includes('redirecting to login') || errorMessage.includes('redirect login')) {
          // Redirect is happening, don't set error or loading states
          return;
        }
        
        if (errorMessage.includes('Unauthorized')) {
          setAuthReady(false);
          setLoading(false);
        } else if (errorMessage.includes('Network error')) {
          setUserInfo(null);
          setAuthReady(false);
          setError('Unable to connect. Please check your internet connection and try again.');
          setLoading(false);
        } else if (errorMessage.includes('Server error')) {
          setAuthReady(false);
          setError('Server temporarily unavailable. Please try again in a moment.');
          setLoading(false);
        } else {
          setUserInfo(null);
          setAuthReady(false);
          setError('Failed to load user information. Please try again.');
          setLoading(false);
        }
      } finally {
        // Always set loading to false
        // If redirect is happening, it was already handled with early return in catch block
        setLoading(false);
      }
    };

    initializeAuth();
  }, [isAuthenticated, auth0User, auth0Loading, getAccessTokenSilently]);

  const value = {
    user: auth0User ? {
      sub: auth0User.sub,
      id: auth0User.sub,
      email: auth0User.email,
      name: auth0User.name,
      ...auth0User
    } : null,
    setUser: () => {}, // No-op for compatibility
    userInfo,
    setUserInfo,
    loading,
    authReady,
    error,
    setError,
    isUserAuthenticated,
    getTherapistInfo,
    refreshUserInfo,
    login,
    signup,
    logout,
    isDemoMode,
    toggleDemoMode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
